import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ThemeToggleButton from '../components/ThemeToggleButton';
import { useWebNotification } from '../hooks/useWebNotification';
import axios from 'axios';
import { toast } from 'sonner';
import AdminChat from './AdminChat';
import HandlerProfile from './HandlerProfile';
import StatusManagement from '../components/StatusManagement';
import {
  Chats,
  Gear,
  SignOut,
  Users,
  Megaphone,
  Broadcast,
  UsersThree,
  Star,
  ArrowClockwise,
  Copy,
  WhatsappLogo,
  ChatCircle,
  ArrowsClockwise,
  DotsThreeVertical,
  ChatCircleDots,
  Flag,
  CaretDown,
  Eye
} from '@phosphor-icons/react';
import MBSetupBadge, { MB_SETUPS, useAvailableMBs } from '../components/MBSetupBadge';
import BulkAssignMBModal from '../components/BulkAssignMBModal';
import BadgeDetailsView from '../components/BadgeDetailsView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

import { startPolling, onVisibilityFetch } from '../utils/pollingKeepAlive';
import { connectSocket, onCustomerRegistered, offCustomerRegistered } from '../utils/socket';
import useResizableSidebar from '../hooks/useResizableSidebar';
import ResizeHandle from '../components/ResizeHandle';

const AdminDashboard = () => {
  const { sidebarWidth, isDragging, startDragging } = useResizableSidebar(380, 260, 650);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification, playNotificationSound, updateAppBadge } = useWebNotification();
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'deposit');
  const [badges, setBadges] = useState({ pw: 0, n: 0, g: 0, pt: 0, groups_pending: {} });
  const [showGroupsMenu, setShowGroupsMenu] = useState(false);
  const [showGBarMenu, setShowGBarMenu] = useState(false);
  const [showNMenu, setShowNMenu] = useState(false);
  const [nList, setNList] = useState([]);
  const [loadingNList, setLoadingNList] = useState(false);
  const nMenuRef = useRef(null);
  const [showPtMenu, setShowPtMenu] = useState(false);
  const [ptList, setPtList] = useState([]);
  const [loadingPtList, setLoadingPtList] = useState(false);
  const [ptThreshold, setPtThreshold] = useState(0);
  const [showPtThresholdMenu, setShowPtThresholdMenu] = useState(false);
  const [customPtMin, setCustomPtMin] = useState('');
  const ptMenuRef = useRef(null);
  const [activeBadgeView, setActiveBadgeView] = useState(null);
  const [activeProfileCustomerId, setActiveProfileCustomerId] = useState(null);
  const [activeProfileRoomId, setActiveProfileRoomId] = useState(null);
  const groupsMenuRef = useRef(null);
  const gBarMenuRef = useRef(null);
  const headerMbDropdownRef = useRef(null);
  const mbDropdownRef = useRef(null);
  const [showHeaderMbMenu, setShowHeaderMbMenu] = useState(false);
  const [showMbDropdown, setShowMbDropdown] = useState(false);
  const [supportSubTab, setSupportSubTab] = useState('support'); // 'support' or 'inquiry'
  const [mbFilter, setMbFilter] = useState('all'); // 'all' | dynamic MBs
  const availableMBs = useAvailableMBs();
  const [showBulkMB, setShowBulkMB] = useState(false);
  const [rooms, setRooms] = useState(() => {
    try {
      const cached = sessionStorage.getItem('admin_rooms_cache');
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });
  const [groups, setGroups] = useState(() => {
    try {
      const cached = sessionStorage.getItem('admin_groups_cache');
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });
  const [filteredRooms, setFilteredRooms] = useState([]);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [loading, setLoading] = useState(() => {
    // If we have cached data, don't show loading spinner
    const cached = sessionStorage.getItem('admin_rooms_cache');
    return !cached;
  });
  const [unreadCounts, setUnreadCounts] = useState({}); // {room_id: count}
  const [appName, setAppName] = useState('DWS Vault Chat');
  const [inquiryAnalytics, setInquiryAnalytics] = useState({ today: 0, last_7_days: 0, total: 0 });
  const [inquiryList, setInquiryList] = useState([]);
  const [inquiryListTab, setInquiryListTab] = useState('all');
  const [inquiryHasMore, setInquiryHasMore] = useState(true);
  const [inquiryLoadingMore, setInquiryLoadingMore] = useState(false);
  const inquiryListRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState('');
  // Keep the legacy date-filter state for reports, but show the complete paginated list by default.
  const [inquiryFilter, setInquiryFilter] = useState('total'); // 'today', '7days', 'total'

  useEffect(() => {
    axios.get(`${API}/settings/pt-threshold`)
      .then((res) => setPtThreshold(Number(res.data?.minutes) || 0))
      .catch(() => {});
  }, []);

  const savePtThreshold = async (minutes) => {
    try {
      const res = await axios.put(`${API}/settings/pt-threshold`, { minutes });
      setPtThreshold(Number(res.data?.minutes) || 0);
      setShowPtThresholdMenu(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save PT threshold');
    }
  };
  
  // Selection and deletion states
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [showClientListModal, setShowClientListModal] = useState(false);
  const [touchStartPos, setTouchStartPos] = useState(null);
  const [isDesktop, setIsDesktop] = useState(() => {
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    return !isPWA && window.innerWidth >= 1024;
  });
  const [selectedChatId, setSelectedChatId] = useState(location.state?.selectedChatId || null);
  const [bottomTab, setBottomTab] = useState('chats');
  const [showMenu, setShowMenu] = useState(false);
  const [copyPopupChat, setCopyPopupChat] = useState(null);
  const [filterTab, setFilterTab] = useState('all');
  const menuRef = useRef(null);
  const [visibleChatsCount, setVisibleChatsCount] = useState(15);
  const [isLoadingMoreChats, setIsLoadingMoreChats] = useState(false);

  // Reset pagination to 15 when changing tabs, filters, or search
  useEffect(() => {
    setVisibleChatsCount(15);
  }, [activeTab, supportSubTab, filterTab, mbFilter, debouncedSearchQuery]);

  const handleAdminListScroll = (event) => {
    const element = event.currentTarget;
    if (element.scrollHeight - element.scrollTop - element.clientHeight < 120) {
      if (visibleChatsCount < filteredRooms.length && !isLoadingMoreChats) {
        setIsLoadingMoreChats(true);
        setTimeout(() => {
          setVisibleChatsCount(prev => Math.min(prev + 15, filteredRooms.length));
          setIsLoadingMoreChats(false);
        }, 150);
      }
    }
  };
  
  // Notification tracking
  const prevUnreadCountsRef = useRef({});
  const isFirstLoadRef = useRef(true);
  const notifiedRoomsRef = useRef(new Set());

  useEffect(() => {
    if (location.state?.selectedChatId) {
      const roomId = location.state.selectedChatId;
      if (isDesktop) {
        setSelectedChatId(roomId);
      } else {
        navigate(`/admin-chat/${roomId}`, { state: { activeTab: location.state.activeTab, supportSubTab: location.state.supportSubTab, mbFilter: location.state.mbFilter } });
      }
    }
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
    if (location.state?.supportSubTab) {
      setSupportSubTab(location.state.supportSubTab);
    }
    if (location.state?.mbFilter) {
      setMbFilter(location.state.mbFilter);
    }
  }, [location.state, isDesktop]);

  // Auto-close profile drawer when selected chat or tab changes
  useEffect(() => {
    setActiveProfileCustomerId(null);
    setActiveProfileRoomId(null);
  }, [selectedChatId, activeTab]);

  // Desktop detection (disabled in PWA mode)
  useEffect(() => {
    const handleResize = () => {
      const isPWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
      setIsDesktop(!isPWA && window.innerWidth >= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch app name from settings
  useEffect(() => {
    const fetchAppName = async () => {
      try {
        const response = await axios.get(`${API}/settings/support-info`);
        if (response.data.app_name) {
          setAppName(response.data.app_name);
          document.title = `${response.data.app_name} - Admin`;
        }
      } catch (error) {
        console.error('Failed to fetch app name:', error);
      }
    };
    
    fetchAppName();
    // Poll every 10 seconds
    const interval = setInterval(fetchAppName, 10000);
    return () => clearInterval(interval);
  }, []);


  // ========== ONLINE STATUS HEARTBEAT ==========
  // Mark admin as online when on dashboard (critical for delivery status)
  useEffect(() => {
    if (!user?.id) return;
    
    // Connect socket for real-time notifications
    connectSocket(user.id);
    
    // Set online immediately when dashboard loads
    axios.post(`${API}/users/online-status?user_id=${user.id}`, {
      is_online: true
    }).catch(err => console.error('❌ Online status update failed:', err));
    
    // Heartbeat every 30s — sufficient to maintain online presence
    const heartbeatInterval = setInterval(() => {
      axios.post(`${API}/users/online-status?user_id=${user.id}`, {
        is_online: true
      }).catch(err => console.error('❌ Heartbeat failed:', err));
    }, 30000);
    
    // Set offline when leaving dashboard
    return () => {
      clearInterval(heartbeatInterval);
      axios.post(`${API}/users/online-status?user_id=${user.id}`, {
        is_online: false
      }).catch(err => console.error('❌ Offline status update failed:', err));
    };
  }, [user?.id]);

  // Real-time synchronization when a customer is registered
  useEffect(() => {
    const handleCustomerRegistered = (data) => {
      if (!data) return;
      setRooms(prevRooms => prevRooms.map(r => {
        if (r.id === data.roomId || r.room_id === data.roomId || (data.customerId && r.customer_id === data.customerId)) {
          return {
            ...r,
            customer_name: data.name || r.customer_name,
            customer_phone: data.phone || r.customer_phone,
            customer_is_guest: false,
            is_guest: false
          };
        }
        return r;
      }));
    };
    onCustomerRegistered(handleCustomerRegistered);
    return () => offCustomerRegistered(handleCustomerRegistered);
  }, []);

  useEffect(() => {
    const fetchBadges = async () => {
      try {
        const res = await axios.get(`${API}/chat/header-badges`);
        setBadges(res.data);
      } catch (err) {
        console.error('Failed to fetch header badges:', err);
      }
    };
    
    fetchBadges();
    const interval = setInterval(fetchBadges, 8000);
    window.addEventListener('refresh-header-badges', fetchBadges);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('refresh-header-badges', fetchBadges);
    };
  }, []);

  useEffect(() => {
    const handleMarkedRead = (e) => {
      if (e.detail?.roomId) {
        setUnreadCounts(prev => ({
          ...prev,
          [e.detail.roomId]: 0
        }));
      }
    };
    window.addEventListener('messages-marked-read', handleMarkedRead);
    return () => window.removeEventListener('messages-marked-read', handleMarkedRead);
  }, []);

  useEffect(() => {
    const clickOutside = (e) => {
      if (groupsMenuRef.current && !groupsMenuRef.current.contains(e.target)) {
        setShowGroupsMenu(false);
      }
      if (gBarMenuRef.current && !gBarMenuRef.current.contains(e.target)) {
        setShowGBarMenu(false);
      }
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
      if (headerMbDropdownRef.current && !headerMbDropdownRef.current.contains(e.target)) {
        setShowHeaderMbMenu(false);
      }
      if (mbDropdownRef.current && !mbDropdownRef.current.contains(e.target)) {
        setShowMbDropdown(false);
      }
    };
    document.addEventListener('mousedown', clickOutside);
    return () => document.removeEventListener('mousedown', clickOutside);
  }, []);

  useEffect(() => {
    loadRooms();
    loadUnreadCounts();
    loadInquiryData();
    // Background-safe polling using Web Worker
    const stopPoll = startPolling('admin-dashboard', () => {
      loadRooms();
      loadUnreadCounts();
      if (activeTab === 'inquiry') loadInquiryData();
    }, 3000);
    const cleanupVis = onVisibilityFetch(() => {
      loadRooms();
      loadUnreadCounts();
    });
    return () => { stopPoll(); cleanupVis(); };
  }, [activeTab, selectedDate, inquiryFilter, selectedChatId]);

  const loadInquiryData = async ({ append = false } = {}) => {
    if (append && (inquiryLoadingMore || !inquiryHasMore)) return;
    if (append) setInquiryLoadingMore(true);
    try {
      // Load analytics
      const analyticsResponse = await axios.get(`${API}/inquiry/analytics`);
      // Smart setState for inquiry analytics
      setInquiryAnalytics(prev => {
        if (JSON.stringify(prev) === JSON.stringify(analyticsResponse.data)) return prev;
        return analyticsResponse.data;
      });
      
      // Load inquiry list based on filter or date
      let listParams = {};
      if (selectedDate) {
        listParams = { date: selectedDate };
      } else if (inquiryFilter === 'today') {
        listParams = { date: new Date().toISOString().split('T')[0] };
      } else if (inquiryFilter === '7days') {
        listParams = { days: 7 };
      }
      // 'total' means no filter - get all
      
      const currentOffset = append ? inquiryList.length : 0;
      const requestedLimit = append ? 15 : Math.max(15, inquiryList.length || 15);
      const listResponse = await axios.get(`${API}/inquiry/list`, {
        params: { ...listParams, limit: requestedLimit, offset: currentOffset }
      });
      setInquiryList(prev => {
        const newList = listResponse.data.inquiries || [];
        if (append) {
          const seen = new Set(prev.map(item => item.inquiry_id));
          return [...prev, ...newList.filter(item => !seen.has(item.inquiry_id))];
        }
        if (JSON.stringify(prev) === JSON.stringify(newList)) return prev;
        return newList;
      });
      setInquiryHasMore(Boolean(listResponse.data.pagination?.hasMore));
    } catch (error) {
      console.error('Failed to load inquiry data:', error);
    } finally {
      if (append) setInquiryLoadingMore(false);
    }
  };

  const handleInquiryListScroll = (event) => {
    const element = event.currentTarget;
    if (element.scrollHeight - element.scrollTop - element.clientHeight < 120) {
      loadInquiryData({ append: true });
    }
  };

  // Reload inquiry list when date or filter changes
  useEffect(() => {
    if (activeTab === 'inquiry') {
      loadInquiryData();
    }
  }, [selectedDate, inquiryFilter]);

  const loadRooms = async () => {
    try {
      const response = await axios.get(`${API}/chat/rooms`, {
        params: { user_id: user.id, role: user.role }
      });

      // Smart setState - only update if data changed
      setRooms(prev => {
        const newData = response.data;
        if (JSON.stringify(prev) === JSON.stringify(newData)) return prev;
        sessionStorage.setItem('admin_rooms_cache', JSON.stringify(newData));
        return newData;
      });
      
      // Fetch groups
      const groupsResponse = await axios.get(`${API}/groups/user-groups`, {
        params: { user_id: user.id }
      });
      setGroups(prev => {
        const newData = Array.isArray(groupsResponse.data)
          ? groupsResponse.data
          : Array.isArray(groupsResponse.data?.groups)
          ? groupsResponse.data.groups
          : [];
        if (JSON.stringify(prev) === JSON.stringify(newData)) return prev;
        sessionStorage.setItem('admin_groups_cache', JSON.stringify(newData));
        return newData;
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to load rooms:', error);
      setLoading(false);
    }
  };

  const loadUnreadCounts = async () => {
    try {
      const response = await axios.get(`${API}/chat/unread-count`, {
        params: { user_id: user.id }
      });
      
      // Convert array to object: {room_id: count}
      const countsMap = {};
      if (response.data.unread_rooms) {
        response.data.unread_rooms.forEach(item => {
          countsMap[item.room_id] = item.count;
        });
      }

      // Automatically zero out unread count for the room currently open on screen
      if (selectedChatId) {
        countsMap[selectedChatId] = 0;
      }
      
      // ========== NOTIFICATION LOGIC ==========
      // Load notified rooms from localStorage (persist across navigation)
      const storedNotified = localStorage.getItem('admin_notified_rooms');
      if (storedNotified && notifiedRoomsRef.current.size === 0) {
        // Only load from localStorage on first load
        try {
          const parsed = JSON.parse(storedNotified);
          parsed.forEach(roomId => notifiedRoomsRef.current.add(roomId));
        } catch (e) {
          // Invalid JSON, ignore
        }
      }
      
      // Check for NEW unread messages and trigger notification
      if (isFirstLoadRef.current) {
        console.log('📋 First load - skipping notification, setting baseline');
        isFirstLoadRef.current = false;
      } else {
        const prevCounts = prevUnreadCountsRef.current;
        console.log('🔍 Checking for new messages...');
        
        Object.keys(countsMap).forEach(roomId => {
          const currentCount = countsMap[roomId];
          const prevCount = prevCounts[roomId] || 0;
          
          // New unread message detected - but not for the chat we're actively viewing
          if (currentCount > prevCount && roomId !== selectedChatId) {
            console.log(`🔔 NEW MESSAGE! Room ${roomId.substring(0,8)}... : prev=${prevCount}, new=${currentCount}`);
            
            // Find room details for better notification
            const room = rooms.find(r => r.id === roomId);
            const customerName = room?.customer_name || room?.customer?.username || 'Customer';
            const chatType = room?.chat_type || 'chat';
            
            // Show notification with sound - ALWAYS play sound for new message
            showNotification({
              title: `${customerName} - ${chatType.toUpperCase()}`,
              body: 'New message received',
              tag: `admin-dashboard-${roomId}-${Date.now()}`, // Unique tag to ensure new notification
              data: {
                roomId: roomId,
                chatType: chatType
              }
            });
          }
        });
      }
      
      // Update prev counts ref AFTER notification check
      prevUnreadCountsRef.current = { ...countsMap };
      
      setUnreadCounts(countsMap);
      
      // Update PWA badge with total unread count
      const totalUnread = Object.values(countsMap).reduce((sum, c) => sum + c, 0);
      updateAppBadge(totalUnread);
    } catch (error) {
      console.error('Failed to load unread counts:', error);
    }
  };

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Filter rooms based on debounced search
  useEffect(() => {
    let filtered = [];
    
    if (activeTab === 'inquiry') {
      // Inquiry analytics tab - no chat list
      filtered = [];
    } else if (filterTab === 'archive') {
      // If archived filter tab is active, show only archived rooms for this chat type
      if (activeTab === 'support') {
        if (supportSubTab === 'support') {
          filtered = rooms.filter(room => room.chat_type === 'support' && room.is_archived === true);
        } else {
          filtered = rooms.filter(room => room.chat_type === 'inquiry' && room.is_archived === true);
        }
      } else {
        filtered = rooms.filter(room => room.chat_type === activeTab && room.is_archived === true);
      }
    } else if (activeTab === 'archive') {
      filtered = rooms.filter(room => room.is_archived === true);
    } else if (activeTab === 'support') {
      // Support tab has sub-tabs
      if (supportSubTab === 'support') {
        filtered = rooms.filter(room => !room.is_archived && room.mb_setup && room.mb_setup !== 'N' && room.mb_setup !== 'Unassigned' && (room.chat_type === 'support' || room.chat_type === 'inquiry'));
      } else {
        filtered = rooms.filter(room => !room.is_archived && room.chat_type === 'inquiry');
      }
    } else {
      filtered = rooms.filter(room => room.chat_type === activeTab && !room.is_archived && room.mb_setup && room.mb_setup !== 'N' && room.mb_setup !== 'Unassigned');
    }
    
    // Apply filter tab
    if (filterTab === 'unread') {
      filtered = filtered.filter(room => unreadCounts[room.id] > 0);
    } else if (filterTab === 'favourites') {
      filtered = filtered.filter(room => room.is_favourite);
    } else if (filterTab === 'groups') {
      // Groups are separate from rooms - use groups array, but EXCLUDE Withdraw Group channels
      filtered = groups
        .filter(g => !g.name?.toLowerCase().includes('withdraw group'))
        .map(g => ({
          ...g,
          id: g.id,
          customer_name: g.name,
          chat_type: 'groups',
          is_group: true,
          updated_at: g.updated_at || g.created_at
        }));
    }
    
    // Apply search filter if present
    if (debouncedSearchQuery) {
      filtered = filtered.filter(room =>
        room.customer_name?.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
        room.customer_phone?.includes(debouncedSearchQuery)
      );
    }

    // MB Setup filter — applies only within withdrawal tab
    if (['deposit', 'withdrawal', 'support'].includes(activeTab) && filterTab !== 'groups' && mbFilter !== 'all') {
      filtered = filtered.filter(room => room.mb_setup === mbFilter);
    }

    // Deduplicate rooms by customer_id to prevent duplicate chat cards
    const seenCustomers = new Set();
    filtered = filtered.filter(room => {
      if (room.is_group) return true;
      const key = room.customer_id || room.id;
      if (seenCustomers.has(key)) return false;
      seenCustomers.add(key);
      return true;
    });

    // Smart setState - only update if data actually changed
    setFilteredRooms(prev => {
      if (JSON.stringify(prev) === JSON.stringify(filtered)) return prev;
      return filtered;
    });
  }, [debouncedSearchQuery, rooms, activeTab, supportSubTab, filterTab, unreadCounts, mbFilter]);


  const handleChatClick = (roomId) => {
    if (isDesktop) {
      setSelectedChatId(roomId);
    } else {
      navigate(`/admin-chat/${roomId}`, { state: { activeTab, supportSubTab, mbFilter } });
    }
  };

  const handleGroupClick = (groupId) => {
    navigate(`/groups/${groupId}`, { state: { activeTab } });
  };

  const toggleFavourite = async (roomId, currentStatus) => {
    try {
      await axios.post(`${API}/chat/rooms/${roomId}/favourite`, {
        user_id: user.id
      });
      
      // Update local state
      setRooms(prevRooms => 
        prevRooms.map(room => 
          room.id === roomId 
            ? { ...room, is_favourite: !currentStatus }
            : room
        )
      );
      
      toast.success(currentStatus ? 'Removed from favourites' : 'Added to favourites');
    } catch (error) {
      console.error('Toggle favourite error:', error);
      toast.error('Failed to update favourite');
    }
  };

  // Copy client bank details - show popup
  const copyClientDetails = async (room) => {
    setCopyPopupChat(room);
  };

  // Open WhatsApp with client number (PWA-compatible)
  const openWhatsApp = (phone, roomId = null, customerId = null) => {
    if (!phone) {
      toast.error('No phone number available');
      return;
    }
    if (roomId || customerId) {
      axios.post(`${API}/calling-reports/activity`, {
        room_id: roomId,
        customer_id: customerId,
        kind: 'whatsapp'
      }).catch(e => console.error('Error logging WhatsApp activity:', e));
    }
    const cleanNumber = phone.replace(/\D/g, '');
    const whatsappNumber = cleanNumber.startsWith('91') ? cleanNumber : `91${cleanNumber}`;
    const link = document.createElement('a');
    link.href = `https://wa.me/${whatsappNumber}`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (nMenuRef.current && !nMenuRef.current.contains(event.target)) {
        setShowNMenu(false);
      }
      if (groupsMenuRef.current && !groupsMenuRef.current.contains(event.target)) {
        setShowGroupsMenu(false);
      }
      if (ptMenuRef.current && !ptMenuRef.current.contains(event.target)) {
        setShowPtMenu(false);
        setShowPtThresholdMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleNMenu = async () => {
    if (!showNMenu) {
      setShowNMenu(true);
      setShowGroupsMenu(false);
      setShowPtMenu(false);
      setLoadingNList(true);
      try {
        const res = await axios.get(`${API}/chat/badge-details/n`);
        setNList(res.data || []);
      } catch (err) {
        console.error('Failed to load unassigned clients:', err);
      } finally {
        setLoadingNList(false);
      }
    } else {
      setShowNMenu(false);
    }
  };

  const handleTogglePtMenu = async () => {
    if (!showPtMenu) {
      setShowPtMenu(true);
      setShowNMenu(false);
      setShowGroupsMenu(false);
      setLoadingPtList(true);
      try {
        const res = await axios.get(`${API}/chat/badge-details/pt`);
        setPtList(res.data || []);
      } catch (err) {
        console.error('Failed to load pending tasks:', err);
      } finally {
        setLoadingPtList(false);
      }
    } else {
      setShowPtMenu(false);
      setShowPtThresholdMenu(false);
    }
  };

  const handleLogout = () => {
    logout(); // Will handle redirect automatically
  };

  // Long-press handlers for selection mode
  const handleLongPressStart = (roomId, e) => {
    e.preventDefault();
    
    // Get touch/mouse position
    const pos = {
      x: e.touches ? e.touches[0].clientX : e.clientX,
      y: e.touches ? e.touches[0].clientY : e.clientY
    };
    setTouchStartPos(pos);
    
    const timer = setTimeout(() => {
      setSelectionMode(true);
      setSelectedRooms([roomId]);
    }, 1000); // 1 second long press (WhatsApp style)
    setLongPressTimer(timer);
  };

  const handleLongPressEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    setTouchStartPos(null);
  };

  const handleTouchMove = (e) => {
    if (!touchStartPos || !longPressTimer) return;
    
    // Get current position
    const currentPos = {
      x: e.touches ? e.touches[0].clientX : e.clientX,
      y: e.touches ? e.touches[0].clientY : e.clientY
    };
    
    // Calculate distance moved
    const distance = Math.sqrt(
      Math.pow(currentPos.x - touchStartPos.x, 2) + 
      Math.pow(currentPos.y - touchStartPos.y, 2)
    );
    
    // Only cancel if moved more than 10px (allows for slight movement)
    if (distance > 10) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
      setTouchStartPos(null);
    }
  };

  const toggleRoomSelection = (roomId) => {
    if (selectedRooms.includes(roomId)) {
      setSelectedRooms(prev => prev.filter(id => id !== roomId));
    } else {
      setSelectedRooms(prev => [...prev, roomId]);
    }
  };

  const handleDeleteRooms = async () => {
    try {
      if (selectedRooms.length === 1) {
        await axios.delete(`${API}/chat/room/${selectedRooms[0]}?user_id=${user.id}`);
      } else {
        await axios.post(`${API}/chat/rooms/bulk-delete`, {
          room_ids: selectedRooms,
          user_id: user.id
        });
      }
      
      setRooms(prev => prev.filter(r => !selectedRooms.includes(r.id)));
      setSelectionMode(false);
      setSelectedRooms([]);
      setShowDeleteConfirm(false);
      
    } catch (error) {
      console.error('Failed to delete rooms:', error);
      toast.error('Failed to delete chats. Please try again.');
    }
  };

  const cancelSelection = () => {
    setSelectionMode(false);
    setSelectedRooms([]);
  };


  const handleToggleFavourite = async () => {
    try {
      for (const roomId of selectedRooms) {
        await axios.post(`${API}/chat/rooms/${roomId}/favourite`, null, {
          params: { user_id: user.id }
        });
      }
      
      // Reload rooms to reflect changes
      loadRooms();
      setSelectionMode(false);
      setSelectedRooms([]);
    } catch (error) {
      console.error('Failed to toggle favourite:', error);
      toast.error('Failed to toggle favourite');
    }
  };

  const handleToggleArchive = async () => {
    try {
      const isUnarchiving = activeTab === 'archive' || filterTab === 'archive';
      
      for (const roomId of selectedRooms) {
        await axios.post(`${API}/chat/rooms/${roomId}/archive`, null, {
          params: { user_id: user.id, unarchive: isUnarchiving }
        });
      }
      
      toast.success(isUnarchiving ? 'Chats unarchived' : 'Chats archived');
      
      // Reload rooms to reflect changes
      loadRooms();
      setSelectionMode(false);
      setSelectedRooms([]);
    } catch (error) {
      console.error('Failed to toggle archive:', error);
      toast.error('Failed to archive chats');
    }
  };

  const handleAddToClientList = async () => {
    try {
      let successCount = 0;
      let errorCount = 0;
      let alreadyExistsCount = 0;
      
      for (const roomId of selectedRooms) {
        try {
          const response = await axios.post(`${API}/chat/room/add-to-client-list`, {
            room_id: roomId,
            user_id: user.id
          });
          
          if (response.data.message === "Already in client list") {
            alreadyExistsCount++;
          } else {
            successCount++;
          }
        } catch (err) {
          console.error(`Failed to add room ${roomId}:`, err);
          errorCount++;
        }
      }
      
      // Show appropriate toast messages
      if (successCount > 0) {
        toast.success(`${successCount} chat${successCount > 1 ? 's' : ''} added to client list!`);
      }
      
      if (alreadyExistsCount > 0) {
        toast.info(`${alreadyExistsCount} chat${alreadyExistsCount > 1 ? 's are' : ' is'} already in client list`);
      }
      
      if (errorCount > 0) {
        toast.error(`Failed to add ${errorCount} chat${errorCount > 1 ? 's' : ''}`);
      }
      
      setShowClientListModal(false);
      setSelectionMode(false);
      setSelectedRooms([]);
      
    } catch (error) {
      console.error('Failed to add to client list:', error);
      toast.error('Failed to add to client list. Please try again.');
    }
  };


  // Mini component to show bank preview in copy popup
  const CopyPopupBankPreview = ({ customerId, phone }) => {
    const [bankData, setBankData] = React.useState(null);
    React.useEffect(() => {
      axios.get(`${API}/clients/registered`).then(res => {
        const client = res.data.find(c => c.id === customerId || c.phone === phone);
        if (client) setBankData(client);
      }).catch(() => {});
    }, [customerId, phone]);
    if (!bankData) return <p className="text-xs text-wa-text-secondary">Loading bank details...</p>;
    if (!bankData.bank_name && !bankData.bank_account_number) return <p className="text-xs text-wa-text-secondary">No bank details saved</p>;
    return (
      <div className="text-xs text-wa-text-secondary space-y-0.5">
        {bankData.bank_name && <p>Bank: {bankData.bank_name}</p>}
        {bankData.account_holder_name && <p>Holder: {bankData.account_holder_name}</p>}
        {bankData.bank_account_number && <p>A/c: {bankData.bank_account_number}</p>}
        {bankData.ifsc_code && <p>IFSC: {bankData.ifsc_code}</p>}
        {bankData.upi_id && <p>UPI: {bankData.upi_id}</p>}
      </div>
    );
  };

  return (
    <div className="h-screen flex flex-col bg-wa-hover overflow-hidden">
      {/* Full Width Top Header */}
      <div className="bg-[#008069] text-white p-2.5 sm:p-3 flex-shrink-0 relative z-[600] w-full">
        <div className="flex items-center justify-between gap-1 sm:gap-2">
          <div className="cursor-pointer flex items-center gap-1.5 flex-shrink-0" onClick={() => { setSelectedChatId(null); setBottomTab('chats'); navigate('/admin-dashboard'); }}>
            <img src="/icons/icon-128x128.png" alt="DWS" className="w-7 h-7 sm:w-8 sm:h-8 rounded-full" />
            <div className="hidden sm:block">
              <h1 className="font-bold leading-tight text-xs sm:text-sm">DWS Support</h1>
              <p className="text-[9px] sm:text-[10px] text-white/70">Admin Panel</p>
            </div>
          </div>

          {/* Header Center: All MBs Oval Pill Dropdown */}
          <div className="flex-1 flex flex-col items-center justify-center min-w-0 px-1">
            <div className="relative" ref={headerMbDropdownRef}>
              <button
                onClick={() => setShowHeaderMbMenu(!showHeaderMbMenu)}
                className="px-3 py-0.5 sm:py-1 rounded-full border-2 border-emerald-300 bg-white/10 hover:bg-white/20 text-white font-black text-xs flex items-center gap-1 cursor-pointer transition-all shadow-2xs"
              >
                <span>{mbFilter === 'all' ? 'All MBs' : mbFilter}</span>
                <CaretDown size={12} weight="bold" className={`transition-transform duration-200 ${showHeaderMbMenu ? 'rotate-180' : ''}`} />
              </button>

              {showHeaderMbMenu && (
                <div className="absolute top-8 left-1/2 -translate-x-1/2 bg-white dark:bg-[#111b21] border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-[999] w-48 py-1.5 text-xs font-semibold text-slate-800 dark:text-slate-100 animate-in fade-in slide-in-from-top-1 duration-150">
                  <button
                    onClick={() => { setMbFilter('all'); setShowHeaderMbMenu(false); }}
                    className={`w-full px-3.5 py-1.5 text-left hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-between border-b border-gray-100 dark:border-gray-800 ${mbFilter === 'all' ? 'font-black text-emerald-600' : ''}`}
                  >
                    <span>All MBs</span>
                    <span className="bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 rounded-full font-bold text-[10px]">
                      {new Set(rooms.filter(r => (activeTab === 'support' ? (r.chat_type === 'support' || r.chat_type === 'inquiry') : r.chat_type === activeTab) && !r.is_archived).map(r => r.customer_id || r.id)).size}
                    </span>
                  </button>
                  <div className="max-h-56 overflow-y-auto divide-y divide-gray-50 dark:divide-gray-800/40">
                    {availableMBs.map((mb) => {
                      const count = new Set(rooms.filter(r => (activeTab === 'support' ? (r.chat_type === 'support' || r.chat_type === 'inquiry') : r.chat_type === activeTab) && !r.is_archived && r.mb_setup === mb).map(r => r.customer_id || r.id)).size;
                      return (
                        <button
                          key={mb}
                          onClick={() => { setMbFilter(mb); setShowHeaderMbMenu(false); }}
                          className={`w-full px-3.5 py-1.5 text-left hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-between transition-colors ${mbFilter === mb ? 'bg-emerald-50 dark:bg-emerald-950/60 font-bold text-emerald-600' : ''}`}
                        >
                          <span className="truncate">{mb}</span>
                          <span className="text-[10px] font-semibold text-slate-400">({count})</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <span className="text-[9px] sm:text-[10px] font-bold text-emerald-100 bg-emerald-700/80 px-2 py-0.2 rounded-full mt-0.5 capitalize shadow-2xs">
              {activeTab === 'support' ? `${supportSubTab} panel` : `${activeTab} panel`}
            </span>
          </div>

          <div className="flex items-center gap-1 relative flex-shrink-0">
            {/* Header Badges: S, I, PW, N, G, PT */}
            <div className="flex items-center gap-1 sm:gap-1.5 mr-0.5 sm:mr-1.5">
               {/* S Badge (Support Normal) */}
               <button
                 onClick={() => { setActiveTab('support'); setSupportSubTab('support'); setMbFilter('all'); }}
                 className={`relative w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-[10px] font-black shadow-md transition-all focus:outline-none cursor-pointer ${
                   activeTab === 'support' && supportSubTab === 'support'
                     ? 'bg-teal-600 ring-2 ring-white text-white scale-105'
                     : 'bg-teal-500 hover:bg-teal-600 text-white'
                 }`}
                 title="S: Support Normal Panel"
               >
                 <span>S</span>
               </button>

               {/* I Badge (Guest Inquiry) */}
               <button
                 onClick={() => { setActiveTab('inquiry'); setMbFilter('all'); }}
                 className={`relative w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-[10px] font-black shadow-md transition-all focus:outline-none cursor-pointer ${
                   activeTab === 'inquiry'
                     ? 'bg-purple-700 ring-2 ring-white text-white scale-105'
                     : 'bg-purple-600 hover:bg-purple-700 text-white'
                 }`}
                 title="I: Guest Inquiry Panel"
               >
                 <span>I</span>
               </button>

               {/* PW Badge */}
               <button 
                 onClick={() => setActiveBadgeView('pw')}
                 className="relative w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center text-[10px] font-black shadow-md transition-all focus:outline-none cursor-pointer"
                 title="PW: Pending Withdrawals (>30 min)"
               >
                 <span>PW</span>
                 {badges.pw > 0 && (
                   <span className="absolute -bottom-1 -right-1 bg-red-600 text-white rounded-full text-[9px] font-black w-4 h-4 flex items-center justify-center border border-white shadow-sm animate-pulse">
                    {badges.pw}
                  </span>
                 )}
               </button>

                {/* N Badge (Unassigned Members Dropdown Toggle) */}
                <div className="relative" ref={nMenuRef}>
                  <button 
                    onClick={handleToggleNMenu}
                    className="relative w-8 h-8 rounded-full bg-blue-500 hover:bg-blue-600 text-white flex items-center justify-center text-[10px] font-black shadow-md transition-all focus:outline-none cursor-pointer"
                    title="N: Unassigned Members (No MB Yet)"
                  >
                    <span>N</span>
                    {badges.n > 0 && (
                      <span className="absolute -bottom-1 -right-1 bg-red-600 text-white rounded-full text-[9px] font-black w-4 h-4 flex items-center justify-center border border-white shadow-sm">
                        {badges.n}
                      </span>
                    )}
                  </button>

                  {showNMenu && (
                    <div className="absolute right-[-144px] top-10 bg-white dark:bg-[#111b21] border border-gray-200 dark:border-gray-700/80 text-wa-text-primary rounded-2xl shadow-2xl z-[999] w-[310px] sm:w-[340px] overflow-hidden text-xs animate-in fade-in zoom-in-95 duration-100">
                      {/* Header matching user mockup */}
                      <div className="px-3.5 py-2.5 bg-slate-100/90 dark:bg-[#1f2c34] border-b border-gray-200 dark:border-gray-700/60 flex items-center justify-between">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse flex-shrink-0" />
                          <span className="text-[11px] font-black uppercase tracking-wider text-slate-800 dark:text-slate-100 truncate">
                            NEW CLIENT — NO MB YET
                          </span>
                        </div>
                        <button
                          onClick={() => {
                            setShowNMenu(false);
                            setActiveBadgeView('n');
                          }}
                          className="text-[11px] font-extrabold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/40 hover:bg-blue-100 dark:hover:bg-blue-900/70 px-2.5 py-1 rounded-full border border-blue-200 dark:border-blue-800/60 transition-all flex items-center gap-1 cursor-pointer flex-shrink-0 shadow-2xs"
                        >
                          View all <span className="font-black text-blue-700 dark:text-blue-300">({badges.n || nList.length})</span>
                        </button>
                      </div>

                      {/* List */}
                      <div className="max-h-72 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800/60">
                        {loadingNList ? (
                          <div className="p-4 text-center text-gray-400 text-xs">Loading unassigned clients...</div>
                        ) : nList.length === 0 ? (
                          <div className="p-4 text-center text-gray-400 text-xs italic">No unassigned clients found.</div>
                        ) : (
                          nList.map((item, idx) => (
                            <div key={item.customer_id || idx} className="p-2.5 hover:bg-slate-50 dark:hover:bg-[#182229] flex items-center justify-between gap-2 transition-colors">
                              <div className="flex items-center gap-2.5 min-w-0">
                                <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-950/80 dark:text-blue-300 font-extrabold text-[11px] flex items-center justify-center border border-blue-200 dark:border-blue-800/60 flex-shrink-0">
                                  {idx + 1}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-xs font-bold text-slate-900 dark:text-white truncate">
                                    {item.customer_name}
                                  </p>
                                  <p className="text-[10px] text-slate-400 dark:text-slate-400 truncate">
                                    {item.phone !== 'N/A' ? item.phone : (item.is_new_today ? 'New today' : 'Unassigned')}
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                {/* Assign MB Selector */}
                                <select
                                  key={`n-select-${item.customer_id}`}
                                  value=""
                                  onChange={async (e) => {
                                    const selectedMB = e.target.value;
                                    if (!selectedMB) return;
                                    try {
                                      await axios.put(`${API}/clients/${item.customer_id}/mb-setup`, { mb_setup: selectedMB });
                                      toast.success(`Assigned ${item.customer_name} to ${selectedMB}`);
                                      // Optimistically remove from list & decrement badge count
                                      setNList(prev => prev.filter(c => c.customer_id !== item.customer_id));
                                      setBadges(prev => ({ ...prev, n: Math.max(0, (prev.n || 1) - 1) }));
                                    } catch (err) {
                                      toast.error('Failed to assign MB');
                                    }
                                  }}
                                  className="px-2 py-1 bg-amber-50 hover:bg-amber-100 text-amber-900 dark:bg-amber-950/70 dark:text-amber-200 text-[10px] font-bold rounded-lg border border-amber-300/80 dark:border-amber-700/70 cursor-pointer outline-none transition-colors shadow-2xs"
                                >
                                  <option value="" disabled>🏷️ Assign MB ▾</option>
                                  {availableMBs.map(mb => (
                                    <option key={mb} value={mb}>{mb}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>

               {/* G Badge (Groups Dropdown Toggle) */}
               <div className="relative" ref={groupsMenuRef}>
                 <button 
                   onClick={() => setShowGroupsMenu(!showGroupsMenu)}
                   className="relative w-8 h-8 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center text-[10px] font-black shadow-md transition-all focus:outline-none"
                   title="G: Groups (Pending requests)"
                 >
                   <span>G</span>
                   {badges.g > 0 && (
                    <span className="absolute -bottom-1 -right-1 bg-red-600 text-white rounded-full text-[9px] font-black w-4 h-4 flex items-center justify-center border border-white shadow-sm">
                      {badges.g}
                    </span>
                   )}
                 </button>
                 
                 {showGroupsMenu && (
                   <div className="absolute right-0 top-9 bg-white dark:bg-[#111b21] opacity-100 border border-wa-border text-wa-text-primary rounded-lg shadow-2xl z-[101] w-52 py-2 text-xs font-semibold">
                     <div className="px-3 py-1.5 border-b border-wa-border text-wa-text-secondary uppercase tracking-wider text-[9px] font-bold">
                       WITHDRAW GROUPS - PENDING
                     </div>
                     {availableMBs.map(mb => {
                       const count = badges.groups_pending?.[mb.toLowerCase()] || badges.groups_pending?.[mb] || 0;
                       return (
                         <div 
                           key={mb}
                           onClick={async () => {
                             setShowGroupsMenu(false);
                             try {
                               const res = await axios.get(`${API}/groups/user-groups`);
                               let gObj = res.data.find(gItem => gItem.name.toLowerCase().includes(mb.toLowerCase()));
                               
                               if (!gObj) {
                                 // Create group on-demand
                                 const createRes = await axios.post(`${API}/groups/create`, {
                                   name: `Withdraw Group ${mb}`,
                                   description: `Withdrawal request channel for ${mb}`
                                 });
                                 gObj = createRes.data;
                               }
                               
                               if (gObj && (gObj.id || gObj.group_id)) {
                                 navigate(`/groups/${gObj.id || gObj.group_id}`);
                               } else {
                                 toast.error(`Failed to navigate to group ${mb}`);
                               }
                             } catch (e) {
                               toast.error(`Failed to find or create group ${mb}`);
                             }
                           }}
                           className="px-3 py-2 hover:bg-wa-hover flex items-center justify-between cursor-pointer transition-colors text-left"
                         >
                           <span>{mb}</span>
                           {count > 0 ? (
                             <span className="bg-red-500 text-white rounded-full px-1.5 py-0.5 text-[10px] font-bold">
                               {count}
                             </span>
                           ) : (
                             <span className="text-wa-text-secondary text-[10px] font-normal">clear</span>
                           )}
                         </div>
                       );
                     })}
                   </div>
                 )}
               </div>

               {/* PT Badge Dropdown */}
               <div className="relative" ref={ptMenuRef}>
                 <button 
                   onClick={handleTogglePtMenu}
                   className="relative w-8 h-8 rounded-full bg-amber-500 hover:bg-amber-600 text-white flex items-center justify-center text-[10px] font-black shadow-md transition-all focus:outline-none cursor-pointer"
                   title="PT: Pending Tasks (No Staff Reply)"
                 >
                   <span>PT</span>
                   {badges.pt > 0 && (
                     <span className="absolute -bottom-1 -right-1 bg-red-600 text-white rounded-full text-[9px] font-black w-4 h-4 flex items-center justify-center border border-white shadow-sm">
                       {badges.pt}
                     </span>
                   )}
                 </button>

                 {showPtMenu && (
                   <div className="absolute right-[-72px] sm:right-[-72px] top-10 bg-white dark:bg-[#111b21] border border-gray-200 dark:border-gray-700/80 text-wa-text-primary rounded-2xl shadow-2xl z-[999] w-[310px] sm:w-[340px] overflow-hidden text-xs animate-in fade-in zoom-in-95 duration-100">
                     {/* Header matching user mockup */}
                     <div className="px-3.5 py-2.5 bg-slate-100/90 dark:bg-[#1f2c34] border-b border-gray-200 dark:border-gray-700/60 flex items-center justify-between">
                       <div className="flex items-center gap-1.5 min-w-0">
                         <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse flex-shrink-0" />
                         <span className="text-[11px] font-black uppercase tracking-wider text-slate-800 dark:text-slate-100 truncate">
                           PENDING — AWAITING REPLY
                         </span>
                       </div>

                       {/* Dropdown Selector Trigger Button */}
                       <button
                         onClick={() => setShowPtThresholdMenu(!showPtThresholdMenu)}
                         className="text-[11px] font-extrabold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/40 hover:bg-amber-100 dark:hover:bg-amber-900/70 px-2.5 py-1 rounded-full border border-amber-200 dark:border-amber-800/60 transition-all flex items-center gap-1 cursor-pointer flex-shrink-0 shadow-2xs"
                       >
                         <span>{ptList.filter(item => (item.elapsed_minutes || 0) >= ptThreshold).length} · {ptThreshold}m+</span>
                         <CaretDown size={12} weight="bold" className={`transition-transform duration-200 ${showPtThresholdMenu ? 'rotate-180' : ''}`} />
                       </button>
                     </div>

                     {/* Threshold Selector Sub-Menu */}
                     {showPtThresholdMenu && (
                       <div className="bg-slate-100/80 dark:bg-[#182229] border-b border-gray-200 dark:border-gray-700/60 p-3 space-y-2 animate-in fade-in slide-in-from-top-1 duration-150">
                         <div className="text-[10px] font-black tracking-wider text-slate-500 dark:text-slate-400 uppercase">
                           SHOW CHATS WAITING MORE THAN
                         </div>

                         <div className="grid grid-cols-4 gap-1.5">
                           {[
                             { label: '0m', value: 0 },
                             { label: '15m', value: 15 },
                             { label: '30m', value: 30 },
                             { label: '1h', value: 60 }
                           ].map((preset) => {
                             const isActive = ptThreshold === preset.value;
                             return (
                               <button
                                 key={preset.value}
                                 onClick={() => savePtThreshold(preset.value)}
                                 className={`py-1 px-2 rounded-full text-xs font-bold transition-all cursor-pointer border ${
                                   isActive
                                     ? 'bg-amber-500 text-white border-amber-600 shadow-2xs'
                                     : 'bg-white dark:bg-[#202c33] text-slate-700 dark:text-slate-200 border-gray-200 dark:border-gray-700 hover:bg-amber-50 dark:hover:bg-amber-950/40'
                                 }`}
                               >
                                 {preset.label}
                               </button>
                             );
                           })}
                         </div>

                         {/* Custom Minute Input */}
                         <div className="flex items-center gap-1.5 pt-1">
                           <input
                             type="number"
                             placeholder="Custom min"
                             value={customPtMin}
                             onChange={(e) => setCustomPtMin(e.target.value)}
                             className="flex-1 px-2.5 py-1 bg-white dark:bg-[#202c33] border border-gray-300 dark:border-gray-700 rounded-full text-xs text-slate-900 dark:text-white outline-none focus:border-amber-500 font-medium"
                           />
                           <button
                             onClick={() => {
                               const parsed = parseInt(customPtMin, 10);
                               if (!isNaN(parsed) && parsed >= 0) {
                                 savePtThreshold(parsed);
                               }
                             }}
                             className="px-3 py-1 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs rounded-full transition-colors cursor-pointer shadow-2xs"
                           >
                             Set
                           </button>
                         </div>
                       </div>
                     )}

                     {/* Items List */}
                     <div className="max-h-72 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800/60">
                       {loadingPtList ? (
                         <div className="p-4 text-center text-gray-400 text-xs">Loading pending tasks...</div>
                       ) : ptList.filter(item => (item.elapsed_minutes || 0) >= ptThreshold).length === 0 ? (
                         <div className="p-6 text-center text-slate-400 dark:text-slate-500 text-xs italic flex flex-col items-center justify-center gap-1">
                           <span className="text-base font-bold text-emerald-500">✓</span>
                           <span>All caught up</span>
                         </div>
                       ) : (
                         ptList
                           .filter(item => (item.elapsed_minutes || 0) >= ptThreshold)
                           .slice(0, 10)
                           .map((item, idx) => (
                             <div
                               key={item.room_id || idx}
                               onClick={() => {
                                 setShowPtMenu(false);
                                 handleChatClick(item.room_id);
                               }}
                               className="p-2.5 hover:bg-slate-50 dark:hover:bg-[#182229] flex items-center justify-between gap-2 transition-colors cursor-pointer"
                             >
                               <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                 <div className="w-7 h-7 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200 font-extrabold text-[11px] flex items-center justify-center border border-amber-200 dark:border-amber-800/60 flex-shrink-0">
                                   {idx + 1}
                                 </div>
                                 
                                 <div className="min-w-0 flex-1">
                                   <div className="flex items-center gap-1.5 flex-wrap">
                                     <span className="text-xs font-bold text-slate-900 dark:text-white truncate">
                                       {item.customer_name}
                                     </span>
                                     {item.mb_setup && (
                                       <span className="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 text-[9px] font-black rounded border border-amber-200 dark:border-amber-700/50">
                                         {item.mb_setup}
                                       </span>
                                     )}
                                   </div>
                                   <p className="text-[10px] text-slate-400 dark:text-slate-400 truncate mt-0.5 italic">
                                     {item.last_message_text || 'No message text'}
                                   </p>
                                 </div>
                               </div>

                               <div className="flex-shrink-0 text-right">
                                 <span className="text-[10px] font-black text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/60 px-2 py-0.5 rounded-full border border-amber-200 dark:border-amber-800/60">
                                   {item.elapsed_minutes >= 60
                                     ? `${Math.floor(item.elapsed_minutes / 60)}h ${item.elapsed_minutes % 60}m`
                                     : `${item.elapsed_minutes}m`}
                                 </span>
                               </div>
                             </div>
                           ))
                       )}
                     </div>

                     {/* Bottom View All Button matching N badge style */}
                     <div className="p-2 bg-slate-100/90 dark:bg-[#1f2c34] border-t border-gray-200 dark:border-gray-700/60 flex items-center justify-center">
                       <button
                         onClick={() => {
                           setShowPtMenu(false);
                           setActiveBadgeView('pt');
                         }}
                         className="w-full py-1.5 text-xs font-extrabold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/40 hover:bg-amber-100 dark:hover:bg-amber-900/70 px-3 rounded-full border border-amber-200 dark:border-amber-800/60 transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs"
                       >
                         View all <span className="font-black text-amber-700 dark:text-amber-300">({ptList.filter(item => (item.elapsed_minutes || 0) >= ptThreshold).length})</span>
                       </button>
                     </div>
                   </div>
                 )}
               </div>
             </div>
            <ThemeToggleButton />

            {/* Header 3-dots dropdown menu */}
            <div className="relative" ref={menuRef}>
              <button onClick={() => setShowMenu(!showMenu)} className="p-1.5 sm:p-2 hover:bg-white/10 rounded-full" title="Menu">
                <DotsThreeVertical size={20} weight="bold" />
              </button>
              {showMenu && (
                <div className="absolute right-0 top-10 bg-white dark:bg-[#111b21] opacity-100 border border-wa-border text-wa-text-primary rounded-lg shadow-2xl z-[500] w-52 py-1 text-xs font-semibold">
                  <button onClick={() => { setShowMenu(false); navigate('/groups'); }} className="w-full px-4 py-2.5 text-left text-sm text-wa-text-primary hover:bg-wa-hover flex items-center gap-2">
                    <UsersThree size={18} /> Groups
                  </button>
                  <button onClick={() => { setShowMenu(false); navigate('/broadcast'); }} className="w-full px-4 py-2.5 text-left text-sm text-wa-text-primary hover:bg-wa-hover flex items-center gap-2">
                    <Broadcast size={18} className="flex-shrink-0" /> Broadcast
                  </button>
                  <button onClick={() => { setShowMenu(false); navigate('/client-list'); }} className="w-full px-4 py-2.5 text-left text-sm text-wa-text-primary hover:bg-wa-hover flex items-center gap-2">
                    <Users size={18} /> Clients
                  </button>
                  <button onClick={() => { setShowMenu(false); navigate('/admin/support-analytics'); }} className="w-full px-4 py-2.5 text-left text-sm text-teal-700 hover:bg-teal-50 flex items-center gap-2" data-testid="menu-support-analytics">
                    <ChatCircleDots size={18} /> Support Analytics
                  </button>
                  <button onClick={async () => { setShowMenu(false); toast.info('Refreshing...'); await loadRooms(); await loadUnreadCounts(); if (activeTab === 'inquiry') await loadInquiryData(); toast.success('Refreshed!'); }} className="w-full px-4 py-2.5 text-left text-sm text-wa-text-primary hover:bg-wa-hover flex items-center gap-2">
                    <ArrowClockwise size={18} /> Refresh
                  </button>
                  <button onClick={() => { setShowMenu(false); navigate('/admin-settings'); }} className="w-full px-4 py-2.5 text-left text-sm text-wa-text-primary hover:bg-wa-hover flex items-center gap-2">
                    <Gear size={18} /> Settings
                  </button>
                  <div className="border-t my-1 border-wa-border" />
                  <button onClick={() => { setShowMenu(false); handleLogout(); }} className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-wa-hover flex items-center gap-2">
                    <SignOut size={18} /> Logout
                  </button>
                </div>
              )}
            </div>
        </div>
      </div>
      </div>

      {/* Main Body Layout */}
      <div className="flex-1 flex flex-row overflow-hidden relative w-full">
        {/* Left Panel - Sidebar */}
        <div 
          style={isDesktop ? { width: `${sidebarWidth}px` } : undefined}
          className={`flex flex-col ${isDesktop ? 'border-r border-wa-border flex-shrink-0 z-10' : 'flex-1'} bg-wa-panel h-full overflow-hidden`}
        >

      {/* Support Sub-tabs: Support Normal / Inquiry Support */}
      {activeTab === 'support' && (
        <div className="bg-wa-panel border-b flex flex-shrink-0">
          <button
            onClick={() => setSupportSubTab('support')}
            className={`flex-1 py-2 text-xs font-semibold transition-colors ${
              supportSubTab === 'support'
                ? 'text-purple-600 border-b-2 border-purple-600 bg-wa-hover'
                : 'text-wa-text-secondary hover:text-wa-text-primary'
            }`}
            data-testid="support-normal-tab"
          >
            Support Normal ({new Set(rooms.filter(r => !r.is_archived && (r.chat_type === 'support' || (r.chat_type === 'inquiry' && r.mb_setup && r.mb_setup !== 'N' && r.mb_setup !== 'Unassigned'))).map(r => r.customer_id || r.id)).size})
          </button>
          <button
            onClick={() => setSupportSubTab('inquiry')}
            className={`flex-1 py-2 text-xs font-semibold transition-colors ${
              supportSubTab === 'inquiry'
                ? 'text-orange-600 border-b-2 border-orange-600 bg-wa-hover'
                : 'text-wa-text-secondary hover:text-wa-text-primary'
            }`}
            data-testid="inquiry-support-tab"
          >
            Inquiry Support ({new Set(rooms.filter(r => !r.is_archived && r.chat_type === 'inquiry' && (!r.mb_setup || r.mb_setup === 'N' || r.mb_setup === 'Unassigned')).map(r => r.customer_id || r.id)).size})
          </button>
        </div>
      )}

      {/* Filter Pills Tabs */}
      {activeTab !== 'inquiry' && (
        <div className="bg-wa-panel border-b border-[#e9edef] flex items-center justify-between px-3 py-2 flex-shrink-0">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide no-scrollbar">
            {['all', 'unread', 'favourites', 'groups', 'archive'].map(tab => {
              const count = tab === 'archive' ? rooms.filter(r => r.chat_type === activeTab && r.is_archived).length : 0;
              return (
                <button key={tab} onClick={() => setFilterTab(tab)}
                  className={`text-[13px] px-3 py-[5px] rounded-full whitespace-nowrap transition-colors flex items-center gap-1 ${filterTab === tab ? 'bg-[#e7fce3] text-[#008069] font-medium' : 'bg-wa-panel-header text-wa-icon hover:bg-[#e9edef]'}`}>
                  {tab === 'all' ? 'All' : tab === 'unread' ? 'Unread' : tab === 'favourites' ? 'Favourites' : tab === 'groups' ? 'Groups' : 'Archived'}
                  {tab === 'archive' && count > 0 && <span className="bg-[#008069]/10 text-[#008069] px-1.5 py-0.5 rounded-full text-[10px] font-bold">{count}</span>}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => {
              if (selectionMode) {
                cancelSelection();
              } else {
                setSelectionMode(true);
                setSelectedRooms([]);
              }
            }}
            className={`text-xs px-3 py-1.5 border rounded-lg font-bold transition-colors ${
              selectionMode
                ? 'bg-green-600 text-white border-green-600 hover:bg-green-700'
                : 'bg-wa-hover text-wa-text-primary border-wa-border hover:bg-wa-panel'
            }`}
          >
            {selectionMode ? 'Cancel' : 'Select'}
          </button>
        </div>
      )}

      {/* Search Bar & D / W Quick Switching Badges */}
      <div className="bg-wa-hover p-2.5 border-b flex items-center gap-2">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search customers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-8 py-2 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 text-xs bg-white dark:bg-[#111b21] text-slate-900 dark:text-white"
          />
          <svg
            className="absolute left-2.5 top-2.5 w-4 h-4 text-wa-text-secondary"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-2 text-wa-text-secondary hover:text-wa-text-secondary"
            >
              ✕
            </button>
          )}
        </div>

        {/* D / W Quick Switch Buttons */}
        {activeTab === 'deposit' && (
          <button
            onClick={() => { setActiveTab('withdrawal'); setMbFilter('all'); }}
            className="w-8 h-8 rounded-full bg-red-500 hover:bg-red-600 text-white font-black text-xs flex items-center justify-center flex-shrink-0 shadow-md transition-all cursor-pointer"
            title="Switch to Withdrawal Panel"
          >
            W
          </button>
        )}
        {activeTab === 'withdrawal' && (
          <button
            onClick={() => { setActiveTab('deposit'); setMbFilter('all'); }}
            className="w-8 h-8 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs flex items-center justify-center flex-shrink-0 shadow-md transition-all cursor-pointer"
            title="Switch to Deposit Panel"
          >
            D
          </button>
        )}
        {activeTab === 'support' && user?.role !== 'support_handler' && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => { setActiveTab('deposit'); setMbFilter('all'); }}
              className="w-7 h-7 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-black text-[11px] flex items-center justify-center shadow-md transition-all cursor-pointer"
              title="Switch to Deposit Panel"
            >
              D
            </button>
            <button
              onClick={() => { setActiveTab('withdrawal'); setMbFilter('all'); }}
              className="w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 text-white font-black text-[11px] flex items-center justify-center shadow-md transition-all cursor-pointer"
              title="Switch to Withdrawal Panel"
            >
              W
            </button>
          </div>
        )}
      </div>

      {/* MB Setup Dropdown & Bulk Assign Row */}
      {['deposit', 'withdrawal', 'support'].includes(activeTab) && filterTab !== 'groups' && (
        <div className="bg-wa-panel border-b border-blue-100 flex items-center justify-between gap-2 px-3 py-2 flex-shrink-0 relative z-10" data-testid="mb-tabs-admin">
          {/* All Setups Dropdown Button */}
          <div className="relative" ref={mbDropdownRef}>
            <button
              onClick={() => setShowMbDropdown(!showMbDropdown)}
              className="bg-blue-600 hover:bg-blue-700 text-white text-[12px] px-3 py-1.5 rounded-full font-bold flex items-center gap-1.5 shadow-xs transition-all cursor-pointer"
              data-testid="mb-tab-all-dropdown"
            >
              <span>
                {mbFilter === 'all'
                  ? `All Setups (${new Set(rooms.filter(r => (activeTab === 'support' ? (r.chat_type === 'support' || r.chat_type === 'inquiry') : r.chat_type === activeTab) && !r.is_archived).map(r => r.customer_id || r.id)).size})`
                  : `${mbFilter} (${new Set(rooms.filter(r => (activeTab === 'support' ? (r.chat_type === 'support' || r.chat_type === 'inquiry') : r.chat_type === activeTab) && !r.is_archived && r.mb_setup === mbFilter).map(r => r.customer_id || r.id)).size})`}
              </span>
              <CaretDown size={14} weight="bold" className={`transition-transform duration-200 ${showMbDropdown ? 'rotate-180' : ''}`} />
            </button>

            {showMbDropdown && (
              <div className="absolute top-9 left-0 bg-white dark:bg-[#111b21] border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-[999] w-56 py-1.5 text-xs font-semibold text-slate-800 dark:text-slate-100 animate-in fade-in slide-in-from-top-1 duration-150">
                <button
                  onClick={() => { setMbFilter('all'); setShowMbDropdown(false); }}
                  className={`w-full px-3.5 py-2 text-left hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-between border-b border-gray-100 dark:border-gray-800 ${mbFilter === 'all' ? 'font-bold text-blue-600' : ''}`}
                >
                  <span>All Setups</span>
                  <span className="bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded-full font-bold text-[11px]">
                    {new Set(rooms.filter(r => (activeTab === 'support' ? (r.chat_type === 'support' || r.chat_type === 'inquiry') : r.chat_type === activeTab) && !r.is_archived).map(r => r.customer_id || r.id)).size}
                  </span>
                </button>
                <div className="max-h-60 overflow-y-auto divide-y divide-gray-50 dark:divide-gray-800/40">
                  {availableMBs.map((mb) => {
                    const bucket = rooms.filter(r => (activeTab === 'support' ? (r.chat_type === 'support' || r.chat_type === 'inquiry') : r.chat_type === activeTab) && !r.is_archived && r.mb_setup === mb);
                    const count = new Set(bucket.map(r => r.customer_id || r.id)).size;
                    const unread = bucket.reduce((s, r) => s + (unreadCounts[r.id] || 0), 0);
                    const isActive = mbFilter === mb;
                    return (
                      <button
                        key={mb}
                        onClick={() => { setMbFilter(mb); setShowMbDropdown(false); }}
                        className={`w-full px-3.5 py-2 text-left hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-between transition-colors ${isActive ? 'bg-blue-50/70 dark:bg-blue-950/40 font-bold text-blue-600' : ''}`}
                        data-testid={`mb-tab-${mb}`}
                      >
                        <span className="truncate">{mb}</span>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">({count})</span>
                          {unread > 0 && (
                            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-red-500 text-white">
                              {unread > 99 ? '99+' : unread}
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Bulk Assign Button */}
            {user?.role === 'super_admin' && (
              <button
                onClick={() => setShowBulkMB(true)}
                className="px-3.5 py-1.5 bg-[#008069] hover:bg-[#006e5a] text-white rounded-full text-xs font-bold transition-all shadow-xs flex items-center gap-1 cursor-pointer"
              >
                Bulk Assign
              </button>
            )}
          </div>
        </div>
      )}


      {showBulkMB && (
        <BulkAssignMBModal
          open={showBulkMB}
          onClose={() => setShowBulkMB(false)}
          clients={rooms
            .filter(r => r.chat_type === activeTab && !r.is_archived)
            .map(r => ({
              id: r.customer_id,
              name: r.customer_name,
              phone: r.customer_phone,
              mb_setup: r.mb_setup,
              unread_count: unreadCounts[r.id] || 0,
            }))}
          actorId={user?.id}
          onDone={(ids, mbValue) => {
            setRooms(prev => prev.map(r => (
              ids.includes(r.customer_id) ? { ...r, mb_setup: mbValue } : r
            )));
          }}
        />
      )}

      {bottomTab === 'updates' ? (
        <div className="flex-1 overflow-y-auto bg-wa-hover p-4">
          <h2 className="text-xl font-bold text-wa-text-primary mb-4">Updates</h2>
          <StatusManagement user={user} />
        </div>
      ) : (
      <div onScroll={handleAdminListScroll} className="flex-1 overflow-y-auto">


        {/* Selection Mode Header */}
        {selectionMode && (
          <div className="sticky top-0 bg-green-500 text-white p-3 flex items-center justify-between z-10">
            <div className="flex items-center gap-2">
              <button onClick={cancelSelection} className="hover:bg-green-600 p-1 rounded">
                ← Back
              </button>
              <span className="font-semibold">{selectedRooms.length} selected</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleToggleArchive}
                disabled={selectedRooms.length === 0}
                className="bg-gray-600 hover:bg-gray-700 px-3 py-1 rounded disabled:opacity-50 text-sm"
              >
                📦 {activeTab === 'archive' ? 'Unarchive' : 'Archive'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={selectedRooms.length === 0}
                className="bg-red-500 hover:bg-red-600 px-3 py-1 rounded disabled:opacity-50 text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        )}

        {activeTab === 'inquiry' ? (
          /* Inquiry Analytics Tab - Matching Image 1 */
          <div className="p-4 space-y-4 max-w-2xl mx-auto">
            {/* Top Metric Cards (Today / Last 7 Days / Total) */}
            <div className="hidden grid grid-cols-3 gap-3" aria-hidden="true">
              <button
                onClick={() => { setInquiryFilter('today'); setSelectedDate(''); }}
                className={`bg-[#ff6500] text-white rounded-2xl p-4 text-left relative overflow-hidden transition-all active:scale-95 hover:shadow-lg cursor-pointer ${
                  inquiryFilter === 'today' && !selectedDate ? 'ring-4 ring-orange-300 scale-[1.02]' : ''
                }`}
              >
                <p className="text-xs font-medium text-white/90">Today</p>
                <p className="text-3xl font-extrabold mt-1 text-white">{inquiryAnalytics.today}</p>
              </button>
              <button
                onClick={() => { setInquiryFilter('7days'); setSelectedDate(''); }}
                className={`bg-[#8b5cf6] text-white rounded-2xl p-4 text-left relative overflow-hidden transition-all active:scale-95 hover:shadow-lg cursor-pointer ${
                  inquiryFilter === '7days' && !selectedDate ? 'ring-4 ring-purple-300 scale-[1.02]' : ''
                }`}
              >
                <p className="text-xs font-medium text-white/90">Last 7 Days</p>
                <p className="text-3xl font-extrabold mt-1 text-white">{inquiryAnalytics.last_7_days}</p>
              </button>
              <button
                onClick={() => { setInquiryFilter('total'); setSelectedDate(''); }}
                className={`bg-[#1d70f5] text-white rounded-2xl p-4 text-left relative overflow-hidden transition-all active:scale-95 hover:shadow-lg cursor-pointer ${
                  inquiryFilter === 'total' && !selectedDate ? 'ring-4 ring-blue-300 scale-[1.02]' : ''
                }`}
              >
                <p className="text-xs font-medium text-white/90">Total</p>
                <p className="text-3xl font-extrabold mt-1 text-white">{inquiryAnalytics.total}</p>
              </button>
            </div>

            {/* Shareable Inquiry Link Box */}
            <div className="bg-[#ecfdf5] dark:bg-emerald-950/20 border border-emerald-200/80 dark:border-emerald-800/40 rounded-2xl p-4 shadow-2xs">
              <p className="text-sm font-bold text-emerald-800 dark:text-emerald-300 flex items-center gap-1.5 mb-2.5">
                <span>📎</span> Shareable Inquiry Link
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={`${window.location.origin}/inquiry-chat`}
                  readOnly
                  className="flex-1 text-xs bg-white dark:bg-[#111b21] border border-emerald-200 dark:border-emerald-700/60 rounded-xl px-3 py-2 font-mono text-slate-800 dark:text-slate-200 shadow-2xs"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/inquiry-chat`);
                    toast.success('Link copied!');
                  }}
                  className="bg-[#008069] hover:bg-[#006e5a] text-white font-bold px-4 py-2 rounded-xl text-xs flex items-center gap-1.5 transition-colors shadow-xs cursor-pointer"
                >
                  <span>📋</span> Copy
                </button>
              </div>
            </div>

            <div className="bg-[#ecfdf5] dark:bg-emerald-950/20 border border-emerald-200/80 dark:border-emerald-800/40 rounded-2xl p-4 shadow-2xs mt-3">
              <p className="text-sm font-bold text-emerald-800 dark:text-emerald-300 flex items-center gap-1.5 mb-2.5">
                <span>📱</span> Shareable Inquiry Link (Phone)
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={`${window.location.origin}/support-chat`}
                  readOnly
                  className="flex-1 text-xs bg-white dark:bg-[#111b21] border border-emerald-200 dark:border-emerald-700/60 rounded-xl px-3 py-2 font-mono text-slate-800 dark:text-slate-200 shadow-2xs"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/support-chat`);
                    toast.success('Phone inquiry link copied!');
                  }}
                  className="bg-[#008069] hover:bg-[#006e5a] text-white font-bold px-4 py-2 rounded-xl text-xs flex items-center gap-1.5 transition-colors shadow-xs cursor-pointer"
                >
                  <span>📋</span> Copy
                </button>
              </div>
            </div>

            {/* Filter Section */}
            <div className="hidden bg-white dark:bg-[#111b21] rounded-2xl border border-slate-200/80 dark:border-wa-border p-4 shadow-xs" aria-hidden="true">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-bold text-slate-800 dark:text-white flex items-center gap-2">
                  📅 Filter
                </span>
                {selectedDate && (
                  <button
                    onClick={() => setSelectedDate('')}
                    className="text-xs text-red-500 hover:text-red-700 font-medium flex items-center gap-1"
                  >
                    ✕ Clear Filter
                  </button>
                )}
              </div>

              {/* Quick Filter Pills Row (Flex Wrap, No Scroll View) */}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => { setInquiryFilter('today'); setSelectedDate(''); }}
                  className={`px-4 py-1.5 rounded-full text-xs transition-all cursor-pointer ${
                    inquiryFilter === 'today' && !selectedDate
                      ? 'bg-[#ff6500] text-white font-bold shadow-xs'
                      : 'bg-slate-100 dark:bg-wa-hover text-slate-700 dark:text-slate-300 font-medium hover:bg-slate-200'
                  }`}
                >
                  Today
                </button>
                <button
                  onClick={() => {
                    setInquiryFilter('yesterday');
                    const y = new Date();
                    y.setDate(y.getDate() - 1);
                    setSelectedDate(y.toISOString().split('T')[0]);
                  }}
                  className={`px-4 py-1.5 rounded-full text-xs transition-all cursor-pointer ${
                    inquiryFilter === 'yesterday' || (selectedDate && selectedDate === new Date(Date.now() - 86400000).toISOString().split('T')[0])
                      ? 'bg-[#ff6500] text-white font-bold shadow-xs'
                      : 'bg-slate-100 dark:bg-wa-hover text-slate-700 dark:text-slate-300 font-medium hover:bg-slate-200'
                  }`}
                >
                  Yesterday
                </button>
                <button
                  onClick={() => { setInquiryFilter('7days'); setSelectedDate(''); }}
                  className={`px-4 py-1.5 rounded-full text-xs transition-all cursor-pointer ${
                    inquiryFilter === '7days' && !selectedDate
                      ? 'bg-[#ff6500] text-white font-bold shadow-xs'
                      : 'bg-slate-100 dark:bg-wa-hover text-slate-700 dark:text-slate-300 font-medium hover:bg-slate-200'
                  }`}
                >
                  Weekly
                </button>
                <button
                  onClick={() => { setInquiryFilter('30days'); setSelectedDate(''); }}
                  className={`px-4 py-1.5 rounded-full text-xs transition-all cursor-pointer ${
                    inquiryFilter === '30days' && !selectedDate
                      ? 'bg-[#ff6500] text-white font-bold shadow-xs'
                      : 'bg-slate-100 dark:bg-wa-hover text-slate-700 dark:text-slate-300 font-medium hover:bg-slate-200'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => { setInquiryFilter('custom'); }}
                  className={`px-4 py-1.5 rounded-full text-xs transition-all cursor-pointer ${
                    inquiryFilter === 'custom' || (selectedDate && selectedDate !== new Date(Date.now() - 86400000).toISOString().split('T')[0])
                      ? 'bg-[#ff6500] text-white font-bold shadow-xs'
                      : 'bg-slate-100 dark:bg-wa-hover text-slate-700 dark:text-slate-300 font-medium hover:bg-slate-200'
                  }`}
                >
                  Custom
                </button>
              </div>

              {(inquiryFilter === 'custom' || selectedDate) && (
                <div className="mt-3 pt-3 border-t border-slate-100 dark:border-wa-border">
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => {
                      setSelectedDate(e.target.value);
                      setInquiryFilter('custom');
                    }}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full border border-slate-200 dark:border-wa-border rounded-xl px-3 py-2 text-xs bg-slate-50 dark:bg-wa-hover text-slate-800 dark:text-white focus:bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all cursor-pointer"
                    style={{ colorScheme: 'light' }}
                  />
                  {selectedDate && (
                    <p className="text-xs text-orange-600 mt-1.5 text-center font-medium">
                      Showing inquiries for: {new Date(selectedDate).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Inquiry List Card */}
            <div className="bg-white dark:bg-[#111b21] rounded-2xl border border-slate-200/80 dark:border-wa-border overflow-hidden shadow-xs">
              <div className="p-3.5 border-b border-slate-100 dark:border-wa-border bg-slate-50/50 dark:bg-wa-panel flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h3 className="font-bold text-slate-800 dark:text-white flex items-center gap-2 text-sm">
                    <span>👥</span> Inquiry Chats
                    <span className="text-xs bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300 px-2 py-0.5 rounded-full font-extrabold">
                      {inquiryList.length}
                    </span>
                  </h3>
                  <div className="flex rounded-full bg-slate-100 dark:bg-wa-hover p-0.5" role="tablist" aria-label="Inquiry filter">
                    {['all', 'unread'].map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => setInquiryListTab(tab)}
                        className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${inquiryListTab === tab ? 'bg-white dark:bg-wa-panel text-[#008069] shadow-sm' : 'text-slate-500'}`}
                      >
                        {tab === 'all' ? 'All' : 'Unread'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              {inquiryList.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  <div className="text-4xl mb-2">📭</div>
                  <p className="text-xs font-medium">No inquiries found</p>
                </div>
              ) : (
                <div ref={inquiryListRef} onScroll={handleInquiryListScroll} className="divide-y divide-slate-100 dark:divide-wa-border/50 max-h-[60vh] overflow-y-auto pb-20">
                  {[...inquiryList]
                    .filter((inquiry) => inquiryListTab !== 'unread' || (unreadCounts[inquiry.chat_room_id] || 0) > 0)
                    .sort((a, b) => {
                      // Inquiry chats follow WhatsApp/deposit ordering: latest activity first.
                      // Unread state is only a badge/filter and must not reorder the list.
                      const aTime = new Date(a.last_message_at || a.last_active || a.timestamp || a.created_at || 0).getTime();
                      const bTime = new Date(b.last_message_at || b.last_active || b.timestamp || b.created_at || 0).getTime();
                      return bTime - aTime;
                    })
                    .map((inquiry, idx) => {
                    const roomIdToOpen = inquiry.chat_room_id || inquiry.room_id;
                    const dateVal = inquiry.last_message_at || inquiry.last_active || inquiry.timestamp;
                    const displayName = inquiry.guest_name || 'Guest';
                    const initials = displayName.replace(/\b(Guest|Client)\s*/i, '').trim().slice(0, 2) || 'GU';
                    const unread = unreadCounts[roomIdToOpen] || 0;
                    return (
                      <div
                        key={inquiry.inquiry_id}
                        className="p-3.5 hover:bg-slate-50 dark:hover:bg-wa-hover cursor-pointer transition-colors active:bg-slate-100"
                        onClick={() => roomIdToOpen && (isDesktop ? setSelectedChatId(roomIdToOpen) : navigate(`/admin-chat/${roomIdToOpen}`, { state: { activeTab, supportSubTab, mbFilter } }))}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-[#ff6500] text-white font-extrabold text-base flex items-center justify-center shadow-2xs shrink-0 uppercase">
                              {initials}
                            </div>
                            <div>
                              <p className="font-bold text-slate-900 dark:text-white text-base leading-tight">
                                {inquiry.guest_name}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1 mt-0.5 font-medium">
                                <span>🌐</span> {inquiry.ip_address}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[220px] mt-1">
                                {inquiry.last_message || 'No messages yet'}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-400 font-medium">
                              {dateVal ? new Date(dateVal).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                            </p>
                            {unread > 0 && <span className="inline-flex mt-1 min-w-[20px] h-5 px-1.5 items-center justify-center rounded-full bg-[#008069] text-white text-[10px] font-bold">{unread > 99 ? '99+' : unread}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {inquiryLoadingMore && <div className="p-3 text-center text-xs text-slate-400">Loading more inquiries…</div>}
                </div>
              )}
            </div>
          </div>
        ) : filteredRooms.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-wa-text-secondary">
            <Chats size={64} weight="thin" />
            <p className="mt-4">
              {searchQuery ? `No results found for "${searchQuery}"` : 
               activeTab === 'groups' ? 'No groups yet' :
               activeTab === 'support' && supportSubTab === 'inquiry' ? 'No inquiry chats yet' :
               `No ${activeTab} chats yet`}
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {filteredRooms
              .slice(0, visibleChatsCount)
              .map((room) => (
                <div
                  key={room.id}
                  onClick={() => {
                    if (selectionMode) {
                      toggleRoomSelection(room.id);
                    } else {
                      if (activeTab === 'groups' || filterTab === 'groups' || room.is_group) {
                        handleGroupClick(room.id);
                      } else {
                        handleChatClick(room.id);
                      }
                    }
                  }}
                  onTouchStart={(e) => !selectionMode && handleLongPressStart(room.id, e)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleLongPressEnd}
                  onMouseDown={(e) => !selectionMode && handleLongPressStart(room.id, e)}
                  onMouseMove={handleTouchMove}
                  onMouseUp={handleLongPressEnd}
                  onMouseLeave={handleLongPressEnd}
                  className={`px-3 py-[10px] hover:bg-[#f5f6f6] cursor-pointer transition-colors active:bg-[#e9edef] ${
                    selectedRooms.includes(room.id) 
                      ? 'bg-[#e7fce3]' 
                      : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {selectionMode && (
                      <div className="flex-shrink-0">
                        <input
                          type="checkbox"
                          checked={selectedRooms.includes(room.id)}
                          onChange={() => toggleRoomSelection(room.id)}
                          className="w-5 h-5 text-green-600"
                        />
                      </div>
                    )}
                    {/* Avatar - Handler style */}
                    <div
                      className="w-12 h-12 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center text-white font-bold text-lg"
                      style={
                        activeTab === 'groups'
                          ? room.profile_pic
                            ? { backgroundImage: `url(${room.profile_pic.startsWith('http') ? room.profile_pic : API + room.profile_pic})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundColor: '#4ade80' }
                            : { background: 'linear-gradient(135deg, #4ade80, #16a34a)' }
                          : room.customer_profile_pic
                            ? { backgroundImage: `url(${room.customer_profile_pic?.startsWith('http') ? room.customer_profile_pic : API + room.customer_profile_pic})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundColor: '#00a884' }
                            : { backgroundColor: '#00a884' }
                      }
                    >
                      {activeTab === 'groups'
                        ? !room.profile_pic && <span>{room.name?.charAt(0) || 'G'}</span>
                        : !room.customer_profile_pic && <span className="text-white">{(room.customer_name || 'C')[0].toUpperCase()}</span>
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <h3 className="font-medium text-wa-text-primary truncate text-[16px] flex items-center gap-1">
                            {activeTab === 'groups' ? (room.name || 'Group') : (room.customer_name || 'Customer')}
                            {activeTab !== 'groups' && room.is_suspicious && (
                              <span className="inline-flex items-center gap-0" title="Suspicious Customer">
                                <Flag size={12} weight="fill" className="text-red-500" />
                                <Flag size={12} weight="fill" className="text-red-500" />
                                <Flag size={12} weight="fill" className="text-red-500" />
                              </span>
                            )}
                            {activeTab !== 'groups' && room.is_vip && (
                              <span className="inline-flex items-center gap-0" title="VIP Customer">
                                <Flag size={12} weight="fill" className="text-green-500" />
                                <Flag size={12} weight="fill" className="text-green-500" />
                                <Flag size={12} weight="fill" className="text-green-500" />
                              </span>
                            )}
                          </h3>
                          {activeTab !== 'groups' && <MBSetupBadge value={room.mb_setup} />}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {activeTab !== 'groups' && (
                            <>
                              <button onClick={(e) => { e.stopPropagation(); copyClientDetails(room); }} className="p-0.5 hover:bg-wa-hover rounded" title="Copy client details">
                                <Copy size={14} className="text-wa-text-secondary hover:text-blue-500" />
                              </button>
                              {room.customer_phone && (
                                <button onClick={(e) => { e.stopPropagation(); openWhatsApp(room.customer_phone, room.id, room.customer_id); }} className="p-0.5 hover:bg-wa-hover rounded" title="Open WhatsApp">
                                  <WhatsappLogo size={14} className="text-wa-text-secondary hover:text-green-500" />
                                </button>
                              )}
                            </>
                          )}
                          <span className={`text-[11.5px] ${unreadCounts[room.id] > 0 ? 'text-[#00a884] font-medium' : 'text-wa-text-secondary'}`}>
                            {room.updated_at && !isNaN(new Date(room.updated_at).getTime()) ? new Date(room.updated_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-[2px]">
                        <p className="text-[13.5px] text-wa-text-secondary truncate flex-1 min-w-0">
                          {room.last_message || room.customer_phone || ''}
                        </p>
                        <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                          {(Number(room.pending_forms_count) > 0 || room.withdrawal_action_pending === true) && (
                            <span className="px-2 py-0.5 rounded-full bg-[#f59e0b] text-white font-bold text-[10px] flex items-center gap-1 flex-shrink-0 shadow-sm">
                              <span>🎫</span> {room.pending_forms_count || 1}
                            </span>
                          )}
                          {unreadCounts[room.id] > 0 && (
                            <div className="bg-[#00a884] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                              {unreadCounts[room.id] > 99 ? '99+' : unreadCounts[room.id]}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            {visibleChatsCount < filteredRooms.length && (
              <div className="p-3 text-center text-xs text-wa-text-secondary flex items-center justify-center gap-2">
                <div className="w-3.5 h-3.5 border-2 border-wa-border border-t-emerald-500 rounded-full animate-spin" />
                <span>Loading more chats…</span>
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-wa-panel rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold mb-2">Delete Chats?</h3>
            <p className="text-wa-text-secondary mb-4">
              Are you sure you want to delete {selectedRooms.length} chat{selectedRooms.length > 1 ? 's' : ''}? 
              This will only remove {selectedRooms.length > 1 ? 'them' : 'it'} from your admin panel.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-wa-hover"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteRooms}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copy Details Popup */}
      {copyPopupChat && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setCopyPopupChat(null)}>
          <div className="bg-wa-panel rounded-xl max-w-sm w-full p-5 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="copy-details-popup">
            <h3 className="text-lg font-bold text-wa-text-primary mb-4">Copy Details</h3>
            
            {/* Login Details */}
            <div className="bg-wa-hover rounded-lg p-3 mb-3">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-sm text-wa-text-primary">Login Details</span>
                <button
                  onClick={async () => {
                    let text = `Username: ${copyPopupChat.customer_name || 'N/A'}\nPhone: ${copyPopupChat.customer_phone || 'N/A'}\nPassword: Abcd1234`;
                    try {
                      const settingsRes = await axios.get(`${API}/settings`);
                      const loginLink = settingsRes.data?.customer_login_link;
                      if (loginLink) text += `\nLink: ${loginLink}`;
                    } catch (e) {}
                    navigator.clipboard.writeText(text);
                    toast.success('Login details copied!', { duration: 1500 });
                    setCopyPopupChat(null);
                  }}
                  className="px-3 py-1 bg-[#00a884] text-white text-xs rounded-full hover:bg-[#008f72]"
                >
                  Copy
                </button>
              </div>
              <p className="text-xs text-wa-text-secondary">Username: {copyPopupChat.customer_name || 'N/A'}</p>
              <p className="text-xs text-wa-text-secondary">Phone: {copyPopupChat.customer_phone || 'N/A'}</p>
              <p className="text-xs text-wa-text-secondary">Password: Abcd1234</p>
            </div>

            {/* Bank Details */}
            <div className="bg-wa-hover rounded-lg p-3 mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-sm text-wa-text-primary">Bank Details</span>
                <button
                  onClick={async () => {
                    try {
                      const customerId = copyPopupChat.customer_id;
                      const res = await axios.get(`${API}/clients/registered`);
                      const client = res.data.find(c => c.id === customerId || c.phone === copyPopupChat.customer_phone);
                      if (!client || (!client.bank_name && !client.bank_account_number)) {
                        toast.error('No bank details found'); return;
                      }
                      let text = `User Name: ${client.username || 'N/A'}\n\nA/c Holder Name: ${client.account_holder_name || client.client_name || 'N/A'}\n\nBank Account No: ${client.bank_account_number || 'N/A'}\n\nIFSC Code: ${client.ifsc_code || 'N/A'}\n\nBank Name: ${client.bank_name || 'N/A'}`;
                      if (client.address) text += `\n\nAddress: ${client.address}`;
                      if (client.upi_id) text += `\n\nUPI ID: ${client.upi_id}`;
                      navigator.clipboard.writeText(text);
                      toast.success('Bank details copied!', { duration: 1500 });
                      setCopyPopupChat(null);
                    } catch (e) { toast.error('Failed to fetch bank details'); }
                  }}
                  className="px-3 py-1 bg-[#00a884] text-white text-xs rounded-full hover:bg-[#008f72]"
                >
                  Copy
                </button>
              </div>
              <CopyPopupBankPreview customerId={copyPopupChat.customer_id} phone={copyPopupChat.customer_phone} />
            </div>

            <button onClick={() => setCopyPopupChat(null)} className="w-full py-2.5 border border-wa-border rounded-lg text-wa-text-primary hover:bg-wa-hover">Close</button>
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <div className="bg-wa-panel border-t flex justify-around py-2 flex-shrink-0">
        <button onClick={() => setBottomTab('chats')} className={`flex flex-col items-center px-3 py-1 ${bottomTab === 'chats' ? 'text-[#008069]' : 'text-wa-text-secondary'}`}>
          <Chats size={22} weight={bottomTab === 'chats' ? 'fill' : 'regular'} />
          <span className="text-[10px] mt-0.5">Chats</span>
        </button>
        <button onClick={() => setBottomTab('updates')} className={`flex flex-col items-center px-3 py-1 ${bottomTab === 'updates' ? 'text-[#008069]' : 'text-wa-text-secondary'}`}>
          <ArrowsClockwise size={22} weight={bottomTab === 'updates' ? 'fill' : 'regular'} />
          <span className="text-[10px] mt-0.5">Updates</span>
        </button>
        <button onClick={() => navigate('/groups')} className="flex flex-col items-center px-3 py-1 text-wa-text-secondary">
          <UsersThree size={22} />
          <span className="text-[10px] mt-0.5">Groups</span>
        </button>
        <button onClick={() => navigate('/client-list')} className="flex flex-col items-center px-3 py-1 text-wa-text-secondary">
          <Users size={22} />
          <span className="text-[10px] mt-0.5">Clients</span>
        </button>
        <button onClick={() => navigate('/admin-settings')} className="flex flex-col items-center px-3 py-1 text-wa-text-secondary">
          <Gear size={22} />
          <span className="text-[10px] mt-0.5">Settings</span>
        </button>
      </div>

      </div>{/* End left panel */}

      {/* Resize handle between list and chat (Desktop only) */}
      {isDesktop && (
        <ResizeHandle onMouseDown={startDragging} isDragging={isDragging} />
      )}

      {/* Right Panel - Embedded Chat & Right Contact Profile Drawer (Desktop only) */}
      {isDesktop && (
        <div className="flex-1 h-full relative overflow-hidden bg-[#f0f2f5] dark:bg-[#111b21] flex flex-row">
          <div className="flex-1 h-full relative overflow-hidden flex flex-col">
            {selectedChatId ? (
              <AdminChat
                key={selectedChatId}
                embeddedRoomId={selectedChatId}
                onBackToList={() => setSelectedChatId(null)}
                onOpenProfile={(customerId, roomId) => {
                  setActiveProfileCustomerId(customerId);
                  setActiveProfileRoomId(roomId);
                }}
              />
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-6 text-center select-none">
                <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-white dark:bg-[#202c33] p-4 shadow-sm flex items-center justify-center mb-5 border border-wa-border/50">
                  <img src="/icons/icon-128x128.png" alt="DWS" className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-contain" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-wa-text-primary tracking-tight mb-2">
                  DWS Vault Chat
                </h2>
                <p className="text-xs sm:text-sm text-wa-text-secondary max-w-sm leading-relaxed">
                  Select a chat to start messaging
                </p>
              </div>
            )}
          </div>

          {/* WhatsApp Web Style Right Side Contact Profile Panel */}
          {activeProfileCustomerId && (
            <div className="w-full sm:w-[380px] md:w-[420px] lg:w-[450px] h-full border-l border-wa-border bg-wa-panel flex-shrink-0 z-50 animate-in slide-in-from-right duration-200">
              <HandlerProfile
                customerId={activeProfileCustomerId}
                roomId={activeProfileRoomId}
                onClose={() => {
                  setActiveProfileCustomerId(null);
                  setActiveProfileRoomId(null);
                }}
                isEmbedded={true}
              />
            </div>
          )}
        </div>
      )}
      </div>{/* End Main Body Layout */}
      {/* Badge Details Dashboard Overlay */}
      {activeBadgeView && (
        <BadgeDetailsView
          badgeType={activeBadgeView}
          API={API}
          onClose={() => setActiveBadgeView(null)}
          onOpenChat={(roomId) => {
            if (isDesktop) {
              setSelectedChatId(roomId);
            } else {
              navigate(`/admin-chat/${roomId}`, { state: { activeTab, supportSubTab, mbFilter } });
            }
          }}
        />
      )}

    </div>
  );
};

export default AdminDashboard;
